import React, { useState } from 'react';
import {
	Box,
	Button,
	Text,
	VStack,
	Alert,
	AlertIcon,
	HStack,
	Icon,
	List,
	ListItem,
	ListIcon,
} from '@chakra-ui/react';
import { FaTicketAlt, FaCheckCircle, FaRocket } from 'react-icons/fa';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import { activateAppSumoLicense } from '../../../Api/appsumoApi';
import {
	clearAppSumoSession,
	getAppSumoOAuthCode,
} from '../../../utils/appsumoSession';

const AppSumoLicenseActivateCard = ({ onActivateSuccess }) => {
	const { token } = useAuthenticatedUserInfo();
	const oauthCode = getAppSumoOAuthCode();
	const [error, setError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleActivate = async () => {
		if (!oauthCode) {
			setError(
				'No AppSumo activation link found. Open this page from your AppSumo purchase confirmation.',
			);
			return;
		}

		setError('');
		setIsSubmitting(true);

		try {
			const response = await activateAppSumoLicense({ oauthCode }, token);
			clearAppSumoSession();
			onActivateSuccess?.(response.data?.data);
		} catch (err) {
			setError(
				err.response?.data?.message ||
					err.message ||
					'Unable to activate your AppSumo license.',
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Box
			bg='linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)'
			border='2px solid'
			borderColor='orange.300'
			borderRadius='2xl'
			p={{ base: 5, md: 8 }}
			boxShadow='md'>
			<VStack align='stretch' spacing={5}>
				<HStack spacing={3} align='flex-start'>
					<Icon as={FaTicketAlt} color='orange.500' boxSize={7} mt={0.5} />
					<Box>
						<Text fontWeight='800' fontSize='xl' color='gray.800' m={0}>
							Activate your AppSumo purchase
						</Text>
						<Text fontSize='sm' color='gray.600' m={0} mt={1}>
							You already bought lifetime access on AppSumo. Link it to this
							workspace — no new payment required.
						</Text>
					</Box>
				</HStack>

				<List spacing={2} fontSize='sm' color='gray.700'>
					<ListItem display='flex' alignItems='flex-start'>
						<ListIcon as={FaCheckCircle} color='green.500' mt={0.5} />
						Your AppSumo tier and limits apply immediately
					</ListItem>
					<ListItem display='flex' alignItems='flex-start'>
						<ListIcon as={FaCheckCircle} color='green.500' mt={0.5} />
						Stack additional AppSumo purchases from your AppSumo account
					</ListItem>
				</List>

				{error ? (
					<Alert status='error' borderRadius='lg'>
						<AlertIcon />
						{error}
					</Alert>
				) : null}

				<Button
					colorScheme='orange'
					size='lg'
					borderRadius='xl'
					leftIcon={<FaRocket />}
					onClick={handleActivate}
					isLoading={isSubmitting}
					loadingText='Activating…'
					isDisabled={!oauthCode}>
					Activate lifetime access
				</Button>

				{!oauthCode ? (
					<Text fontSize='xs' color='gray.500' m={0} textAlign='center'>
						Missing activation code? Use the Activate link in your AppSumo account.
					</Text>
				) : null}
			</VStack>
		</Box>
	);
};

export default AppSumoLicenseActivateCard;
